import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Paper,
  useTheme,
  Skeleton,
  Stack,
} from '@mui/material';
import {
  Code as CodeIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Collections as GalleryIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Download as DownloadIcon,
  Language as BrowserIcon,
  Login as LoginIcon,
  AutoAwesome as SparkIcon,
  MenuBook as DocsIcon,
} from '@mui/icons-material';
import useAuth from '../../features/auth/hooks/useAuth';
import HeroInstallCta from '../../features/downloads/components/HeroInstallCta';
import TryInBrowserCta from '../../features/hosted/components/TryInBrowserCta';
import CommunityDownloadsSection from '../../features/downloads/components/CommunityDownloadsSection';
import GalleryCard from '../../features/gallery/components/GalleryCard';
import ReviewsCarousel from '../../features/reviews/components/ReviewsCarousel';
import { fetchGalleryItems, GalleryItem } from '../../features/gallery/api/galleryApi';
import { brand } from '../../config/brand';
import clarityService from '../../services/clarityService';
import LiteYouTube from './LiteYouTube';
import DeferredVideo from './DeferredVideo';

// YouTube video ID for the product demo
const YOUTUBE_VIDEO_ID = 'oJyKbu_n4dA';

/**
 * The path from "interested" to "using the software", in order. Numbered
 * because it is a real sequence — the previous page listed five steps that
 * included copying an API key by hand, which the app no longer needs.
 */
const STEPS = [
  {
    icon: <BrowserIcon />,
    title: 'Try it in your browser',
    body: 'Open a free cloud workspace as a guest — nothing to install, no account. Keep it by signing up, or move on to the desktop app.',
  },
  {
    icon: <DownloadIcon />,
    title: 'Download the app',
    body: 'One installer for Windows, macOS or Linux. No terminal required — the command line is there if you want it.',
  },
  {
    icon: <LoginIcon />,
    title: 'Sign in once',
    body: 'Create a free account inside the app or here. Your API key is provisioned automatically. Nothing to copy or paste.',
  },
  {
    icon: <SparkIcon />,
    title: 'Brief your first agent',
    body: 'Describe the job in plain words. Your agent reads, writes, runs and reports back while you do something else.',
  },
];

const FEATURES = [
  {
    icon: <SecurityIcon />,
    color: 'primary.main',
    title: 'Security-first by default',
    body: 'Every line your agents write follows secure coding patterns and vulnerability prevention, without you asking.',
  },
  {
    icon: <CodeIcon />,
    color: 'secondary.main',
    title: 'Keeps your standards',
    body: 'Agents read your conventions first and produce code that feels native to your project, so it is easy to trust and merge.',
  },
  {
    icon: <SpeedIcon />,
    color: 'success.main',
    title: 'From brief to shipped',
    body: 'Tests, docs and deployment configuration come with the work. Concept to running application in minutes, not sprints.',
  },
];

const HomePage: React.FC = () => {
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const [featuredItems, setFeaturedItems] = useState<GalleryItem[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(true);

  useEffect(() => {
    fetchGalleryItems({ limit: 4, sort: 'popular' })
      .then((res) => setFeaturedItems(res.items))
      .catch(() => {})
      .finally(() => setGalleryLoading(false));
  }, []);

  const accountCta = (placement: string) =>
    isAuthenticated ? (
      <Button
        variant="outlined"
        size="large"
        component={RouterLink}
        to="/dashboard"
        onClick={() => clarityService.track('cta_click', `${placement}_dashboard`)}
        sx={{ fontWeight: 700, textTransform: 'none', px: 3 }}
      >
        Go to your dashboard
      </Button>
    ) : (
      <Button
        variant="outlined"
        size="large"
        component={RouterLink}
        to="/register"
        onClick={() => clarityService.track('cta_click', `${placement}_create_account`)}
        sx={{ fontWeight: 700, textTransform: 'none', px: 3 }}
      >
        Create a free account
      </Button>
    );

  return (
    <Box>
      {/* ───────────────────────── Hero ───────────────────────── */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          color: 'white',
          py: { xs: 6, md: 8 },
          minHeight: { md: 'calc(100vh - 64px)' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <DeferredVideo
          src="/video/onbuzz-agents.mp4"
          poster="/img/onbuzz-agents.webp"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: brand.gradients.hero,
            opacity: 0.85,
            zIndex: 1,
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="overline"
                sx={{ letterSpacing: 2, opacity: 0.85, color: 'white', display: 'block', mb: 1, textAlign: { xs: 'center', md: 'left' } }}
              >
                {brand.fullProductName} · {brand.tagline}
              </Typography>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontSize: { xs: '2.4rem', sm: '3rem', md: '3.6rem' },
                  fontWeight: 800,
                  lineHeight: 1.05,
                  letterSpacing: '-0.02em',
                  color: 'white',
                  textShadow: '0 2px 24px rgba(0,0,0,0.35)',
                  mb: 2,
                  textAlign: { xs: 'center', md: 'left' },
                  textWrap: 'balance',
                }}
              >
                Be the CEO.
              </Typography>
              <Typography
                component="p"
                sx={{
                  mb: 4,
                  opacity: 0.92,
                  fontSize: { xs: '1.05rem', md: '1.25rem' },
                  lineHeight: 1.5,
                  maxWidth: 560,
                  mx: { xs: 'auto', md: 0 },
                  textAlign: { xs: 'center', md: 'left' },
                }}
              >
                Deploy AI teams and agents to handle any workload, move your business faster, and get more done
                with effortless control.
              </Typography>

              {/* Zero-friction entry first: the product in the browser in ~30 s,
                  no install, no account. The installer follows for people ready
                  to commit. */}
              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, mb: 3 }}>
                <TryInBrowserCta tone="onDark" placement="hero" />
              </Box>
              <Typography
                variant="overline"
                sx={{ display: 'block', color: 'rgba(255,255,255,0.7)', letterSpacing: 1.5, mb: 1, textAlign: { xs: 'center', md: 'left' } }}
              >
                or install the desktop app
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <HeroInstallCta tone="onDark" placement="hero" />
              </Box>

              <Stack
                direction="row"
                spacing={1.5}
                sx={{ mt: 3, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}
                useFlexGap
              >
                {isAuthenticated ? (
                  <Button
                    variant="outlined"
                    size="large"
                    component={RouterLink}
                    to="/dashboard"
                    onClick={() => clarityService.track('cta_click', 'hero_dashboard')}
                    sx={{
                      borderColor: 'rgba(255,255,255,0.6)',
                      color: 'white',
                      fontWeight: 700,
                      textTransform: 'none',
                      '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                    }}
                  >
                    Go to your dashboard
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="large"
                    component={RouterLink}
                    to="/register"
                    onClick={() => clarityService.track('cta_click', 'hero_create_account')}
                    sx={{
                      borderColor: 'rgba(255,255,255,0.6)',
                      color: 'white',
                      fontWeight: 700,
                      textTransform: 'none',
                      '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                    }}
                  >
                    Create a free account
                  </Button>
                )}
                <Button
                  variant="text"
                  size="large"
                  component={RouterLink}
                  to="/training"
                  onClick={() => clarityService.track('cta_click', 'hero_tutorials')}
                  sx={{ color: 'white', textTransform: 'none', fontWeight: 600, opacity: 0.9 }}
                >
                  Watch tutorials
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <LiteYouTube videoId={YOUTUBE_VIDEO_ID} title={`${brand.fullProductName} demo`} />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─────────────────── How you get started ─────────────────── */}
      <Container maxWidth="lg" component="section" sx={{ py: { xs: 6, md: 8 } }}>
        <Box textAlign="center" mb={5}>
          <Typography variant="h3" component="h2" gutterBottom fontWeight="bold" sx={{ textWrap: 'balance' }}>
            Up and running in three steps
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 640, mx: 'auto' }}>
            No API keys to copy, no configuration files. Install, sign in, delegate.
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {STEPS.map((step, i) => (
            <Grid item xs={12} md={4} key={step.title}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Box
                  aria-hidden="true"
                  sx={{
                    flex: '0 0 auto',
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: brand.gradients.button,
                    color: brand.buttonTextColor,
                    fontWeight: 800,
                    fontSize: '1.1rem',
                  }}
                >
                  {i + 1}
                </Box>
                <Box>
                  <Typography variant="h6" component="h3" fontWeight={700} gutterBottom>
                    {step.title}
                  </Typography>
                  <Typography color="text.secondary">{step.body}</Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ───────────────── What the agents are good at ───────────────── */}
      <Box component="section" sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={5}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold" sx={{ textWrap: 'balance' }}>
              Multi-agent intelligence, one calm experience
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              {brand.productName} orchestrates specialised agents, each an expert in its domain, to deliver secure,
              production-ready work that follows your project's conventions.
            </Typography>
          </Box>

          {/* Plain tiles: no elevation, no hover lift. The old cards looked
              clickable and collected dead clicks. */}
          <Grid container spacing={4}>
            {FEATURES.map((f) => (
              <Grid item xs={12} md={4} key={f.title}>
                <Box
                  sx={{
                    height: '100%',
                    p: 3,
                    borderLeft: '3px solid',
                    borderColor: f.color,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ color: f.color, mb: 1.5, '& svg': { fontSize: 32 } }}>{f.icon}</Box>
                  <Typography variant="h6" component="h3" gutterBottom fontWeight={700}>
                    {f.title}
                  </Typography>
                  <Typography color="text.secondary">{f.body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* OnBuzz Community (open-source edition) — admin-curated versions from
          GitHub Releases. Below the primary download so the two editions stay
          visibly distinct. */}
      <Container maxWidth="lg" component="section" sx={{ py: 2 }}>
        <CommunityDownloadsSection />
      </Container>

      {/* ───────────────── Command line quick start ───────────────── */}
      <Container maxWidth="lg" component="section" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold" sx={{ textWrap: 'balance' }}>
              Prefer the terminal?
            </Typography>
            <Typography variant="h6" color="text.secondary" paragraph>
              The same agents ship as an npm package with a web UI and a terminal UI. Sign in once; the API key is
              handled for you.
            </Typography>

            <Box component="ol" sx={{ pl: 2.5, mb: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>
                <strong>Install:</strong> <code>{brand.installCommand}</code>
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                <strong>Launch:</strong> <code>{brand.storagePrefix} web</code> for the web UI or{' '}
                <code>{brand.storagePrefix} plus-terminal</code> for the terminal
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                <strong>Sign in</strong> when prompted. That is it.
              </Typography>
            </Box>

            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                size="large"
                component={RouterLink}
                to="/docs"
                startIcon={<DocsIcon />}
                onClick={() => clarityService.track('cta_click', 'quickstart_docs')}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Read the docs
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={RouterLink}
                to="/training"
                onClick={() => clarityService.track('cta_click', 'quickstart_tutorials')}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Video tutorials
              </Button>
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                bgcolor: '#1e1e1e',
                color: '#d4d4d4',
                p: 3,
                borderRadius: 2,
                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                fontSize: '0.85rem',
                border: '1px solid #333',
                overflowX: 'auto',
              }}
            >
              <Typography component="pre" sx={{ m: 0, whiteSpace: 'pre', fontFamily: 'inherit', fontSize: 'inherit' }}>
{`# Install ${brand.productName}
$ ${brand.installCommand}

# Launch the web UI (opens http://localhost:8080)
$ ${brand.storagePrefix} web

# Or the terminal UI
$ ${brand.storagePrefix} plus-terminal

# Reference
${brand.storagePrefix} web             # Web UI in browser
${brand.storagePrefix} terminal        # Terminal UI only
${brand.storagePrefix} plus-terminal   # Server + Terminal UI
${brand.storagePrefix} --port 3000     # Custom port`}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* ───────────────── Community App Gallery ───────────────── */}
      <Box component="section" sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: `rgba(${brand.colorsRgb.fire}, 0.1)`,
                border: `1px solid rgba(${brand.colorsRgb.fire}, 0.2)`,
                borderRadius: 5,
                px: 2,
                py: 0.5,
                mb: 2,
              }}
            >
              <GalleryIcon sx={{ fontSize: 18, color: brand.colors.fire }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: brand.colors.fire, letterSpacing: 1, textTransform: 'uppercase' }}>
                Community showcase
              </Typography>
            </Box>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
              Built with {brand.productName}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Applications the community shipped with their agents. Browse, vote, submit your own.
            </Typography>
          </Box>

          {galleryLoading ? (
            <Grid container spacing={3} justifyContent="center">
              {Array.from({ length: 4 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>
          ) : featuredItems.length > 0 ? (
            <Grid container spacing={3} justifyContent="center">
              {featuredItems.map((item) => (
                <Grid item xs={12} sm={6} md={3} key={item._id}>
                  <GalleryCard item={item} />
                </Grid>
              ))}
            </Grid>
          ) : null}

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 5, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/gallery"
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 4,
                py: 1.5,
                fontWeight: 700,
                textTransform: 'none',
                background: brand.gradients.button,
                color: brand.buttonTextColor,
                '&:hover': { background: brand.gradients.buttonHover },
              }}
            >
              Browse the gallery
            </Button>
            <Button
              variant="outlined"
              size="large"
              component={RouterLink}
              to={isAuthenticated ? '/gallery/submit' : '/register'}
              startIcon={<AddIcon />}
              sx={{
                px: 4,
                py: 1.5,
                fontWeight: 700,
                textTransform: 'none',
                borderColor: brand.colors.fire,
                color: brand.colors.fire,
                '&:hover': { borderColor: brand.colors.fireDark, bgcolor: `rgba(${brand.colorsRgb.fire}, 0.05)` },
              }}
            >
              Submit your app
            </Button>
          </Box>
        </Container>
      </Box>

      {/* What Our Users Say - Reviews Carousel */}
      <ReviewsCarousel />

      {/* ───────────────── Pricing preview ───────────────── */}
      <Box component="section" sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper', py: 10 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={5}>
            <Typography variant="h3" component="h2" gutterBottom fontWeight="bold">
              Simple, transparent pricing
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
              Start free with 100,000 credit points and no card. Every plan includes every AI model.
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {[
              { key: 'scout', name: 'Scout', price: 'Free', credits: '100K', desc: 'To get started', color: '#78909c', highlight: false },
              { key: 'cadet', name: 'Cadet', price: '$19.99', credits: '800K', desc: 'First month free', color: '#42a5f5', highlight: false },
              { key: 'captain', name: 'Captain', price: '$49.99', credits: '2M', desc: 'Most popular', color: brand.colors.fire, highlight: true },
              { key: 'air_marshal', name: 'Air Marshal', price: '$149.99', credits: '6.6M', desc: 'Power users', color: '#ab47bc', highlight: false },
            ].map((plan) => (
              <Grid item xs={6} sm={6} md={3} key={plan.key}>
                <Box
                  component={RouterLink}
                  to="/pricing"
                  onClick={() => clarityService.track('pricing_plan_click', `home_${plan.key}`)}
                  sx={{ textDecoration: 'none', color: 'inherit', display: 'block', height: '100%' }}
                >
                  <Paper
                    elevation={plan.highlight ? 6 : 1}
                    sx={{
                      p: 3,
                      height: '100%',
                      textAlign: 'center',
                      border: plan.highlight ? `2px solid ${plan.color}` : '1px solid',
                      borderColor: plan.highlight ? plan.color : 'divider',
                      position: 'relative',
                      overflow: 'visible',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: 6,
                      },
                    }}
                  >
                    {plan.highlight && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: plan.color,
                          color: 'white',
                          px: 2,
                          py: 0.3,
                          borderRadius: 1,
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        MOST POPULAR
                      </Box>
                    )}
                    <Typography variant="overline" sx={{ color: plan.color, fontWeight: 700, letterSpacing: 1 }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ my: 1 }}>
                      {plan.price}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {plan.price !== 'Free' ? '/month' : 'forever'}
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: plan.color }}>
                      {plan.credits} credits{plan.price === 'Free' ? '' : '/mo'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {plan.desc}
                    </Typography>
                  </Paper>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box textAlign="center" mt={4}>
            <Button
              variant="contained"
              size="large"
              component={RouterLink}
              to="/pricing"
              endIcon={<ArrowForwardIcon />}
              onClick={() => clarityService.track('cta_click', 'home_compare_plans')}
              sx={{
                px: 5,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 700,
                textTransform: 'none',
                background: brand.gradients.button,
                color: brand.buttonTextColor,
                '&:hover': {
                  background: brand.gradients.buttonHover,
                },
              }}
            >
              Compare plans and features
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ───────────────── Closing CTA ───────────────── */}
      <Container maxWidth="md" component="section" sx={{ py: { xs: 8, md: 10 } }}>
        <Box textAlign="center">
          <Typography variant="h3" component="h2" gutterBottom fontWeight="bold" sx={{ textWrap: 'balance' }}>
            Start delegating today
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, textWrap: 'balance' }}>
            Free to try, free to start. Your first agent can be working in the next minute — in your browser, or in the app.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <TryInBrowserCta tone="onLight" placement="closing" />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <HeroInstallCta tone="onLight" compact placement="footer" />
          </Box>
          <Box sx={{ mt: 3 }}>{accountCta('footer')}</Box>
        </Box>
      </Container>
    </Box>
  );
};

export default HomePage;
